import {inject} from "@angular/core";
import {CanDeactivateFn} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {Observable} from "rxjs";
import {ConfirmationComponent} from "../components/confirmation/confirmation.component";

/**
 * Implemented by any routed component that wants to warn the user before navigating away with
 * unsaved edits. The component reports whether it is dirty and may override the prompt title;
 * {@link unsavedChangesGuard} owns the confirmation UX so it stays consistent by default.
 */
export interface HasUnsavedChanges {
    hasUnsavedChanges(): boolean;
    /** Optional override for the confirmation title; defaults to a generic "leave without saving?" prompt. */
    unsavedChangesMessage?: string;
}

/**
 * Generic CanDeactivate guard: when the component reports unsaved changes, it opens a confirmation
 * dialog and only allows navigation if the user confirms. Attach with
 * {@code canDeactivate: [unsavedChangesGuard]} on any route whose component implements
 * {@link HasUnsavedChanges}. Pair with {@code WarnOnUnsavedChangesDirective} to also cover full
 * page unloads (tab close / refresh), which a router guard cannot intercept.
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (component): boolean | Observable<boolean> => {
    if (!component?.hasUnsavedChanges?.()) {
        return true;
    }
    const dialog = inject(MatDialog);
    const dialogRef = dialog.open(ConfirmationComponent);
    dialogRef.componentInstance.confirmMessage = component.unsavedChangesMessage ?? "Leave without saving?";
    dialogRef.componentInstance.htmlContent = "You have unsaved changes that will be lost if you leave this page.";
    return dialogRef.afterClosed();
};
